import React, { useState, useEffect } from 'react';
import { 
  Shield, Plus, Edit, Trash2, Users, Check, X, AlertCircle, Save, Crown
} from 'lucide-react';
import { 
  PERMISSIONS, 
  PREDEFINED_ROLES, 
  PERMISSION_CATEGORIES,
  getPermissionLabel 
} from '../config/permissions';
import {
  getAllRoles,
  createAdminRole,
  updateAdminRole,
  deleteAdminRole,
  assignRoleToUser,
  initializePredefinedRoles
} from '../services/firestoreService';
import { AdminIcon } from './admin/ui/AdminIcon';

const RoleManagement = ({ users = [], onRoleAssigned }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    color: 'blue',
    icon: 'lock',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    const result = await getAllRoles();
    if (result.success) {
      setRoles(result.roles || []);
    }
    setLoading(false);
  };

  const handleInitializeRoles = async () => {
    const result = await initializePredefinedRoles(PREDEFINED_ROLES);
    if (result.success) {
      showMessage('success', result.message);
      fetchRoles();
    } else {
      showMessage('error', result.error);
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.name || !newRole.description) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }

    if (newRole.permissions.length === 0) {
      showMessage('error', 'Please select at least one permission');
      return;
    }

    const result = await createAdminRole({
      ...newRole,
      id: newRole.name.toLowerCase().replace(/\s+/g, '_'),
      level: 50
    });

    if (result.success) {
      showMessage('success', 'Role created successfully');
      setShowCreateModal(false);
      setNewRole({ name: '', description: '', color: 'blue', icon: 'lock', permissions: [] });
      fetchRoles();
    } else {
      showMessage('error', result.error);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;

    const result = await updateAdminRole(selectedRole.id, {
      name: selectedRole.name,
      description: selectedRole.description,
      color: selectedRole.color,
      icon: selectedRole.icon,
      permissions: selectedRole.permissions
    });

    if (result.success) {
      showMessage('success', 'Role updated successfully');
      setShowEditModal(false);
      setSelectedRole(null);
      fetchRoles();
    } else {
      showMessage('error', result.error);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    const result = await deleteAdminRole(roleId);
    if (result.success) {
      showMessage('success', 'Role deleted successfully');
      fetchRoles();
    } else {
      showMessage('error', result.error);
    }
  };

  const handleAssignRole = async (userId, roleId) => {
    const result = await assignRoleToUser(userId, roleId);
    if (result.success) {
      showMessage('success', 'Role assigned successfully');
      if (onRoleAssigned) onRoleAssigned();
    } else {
      showMessage('error', result.error);
    }
  };

  const togglePermission = (permission, isNewRole = true) => {
    if (isNewRole) {
      setNewRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    } else {
      setSelectedRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const getRoleColor = (color) => {
    const colors = {
      red: 'bg-red-100 text-red-800 border-red-200',
      blue: 'bg-[#eaf3ef] text-[#0c0f14] border-[#dcebe4]',
      green: 'bg-green-100 text-green-800 border-green-200',
      purple: 'bg-[#eaf3ef] text-[#0c0f14] border-[#dcebe4]',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      indigo: 'bg-[#eaf3ef] text-[#0c0f14] border-[#dcebe4]',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0c0f14]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#0c0f14]" />
            Role & Permission Management
          </h2>
          <p className="text-gray-600 mt-1">
            Manage admin roles and assign permissions to users
          </p>
        </div>
        <div className="flex gap-3">
          {roles.length === 0 && (
            <button
              onClick={handleInitializeRoles}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Crown className="w-4 h-4" />
              Initialize Roles
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 admin-btn-primary rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Role
          </button>
        </div>
      </div>

      {/* Success/Error Message */}
      {message.text && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <Check className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <p>{message.text}</p>
          </div>
        </div>
      )}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map(role => (
          <div key={role.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#eaf3ef] flex items-center justify-center shrink-0">
                  <AdminIcon name={role.icon || 'lock'} size="lg" className="text-[#0c0f14]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{role.name}</h3>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getRoleColor(role.color)}`}>
                    {role.isSystem ? 'System Role' : 'Custom Role'}
                  </span>
                </div>
              </div>
              {!role.isSystem && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedRole(role);
                      setShowEditModal(true);
                    }}
                    className="p-1 text-[#5c6670] hover:bg-[#f6f7f5] rounded"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteRole(role.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-4">{role.description}</p>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Permissions:</span>
                <span className="font-semibold text-gray-900">
                  {role.permissions?.length || 0}
                </span>
              </div>
              <button
                onClick={() => {
                  setSelectedRole(role);
                  setShowAssignModal(true);
                }}
                className="w-full px-4 py-2 bg-[#eaf3ef] text-[#0c0f14] rounded-lg hover:bg-[#dcebe4] transition-colors flex items-center justify-center gap-2"
              >
                <Users className="w-4 h-4" />
                Assign to Users
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <RoleModal
          title="Create New Role"
          role={newRole}
          onChange={setNewRole}
          onSave={handleCreateRole}
          onClose={() => {
            setShowCreateModal(false);
            setNewRole({ name: '', description: '', color: 'blue', icon: 'lock', permissions: [] });
          }}
          togglePermission={(perm) => togglePermission(perm, true)}
        />
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedRole && (
        <RoleModal
          title="Edit Role"
          role={selectedRole}
          onChange={setSelectedRole}
          onSave={handleUpdateRole}
          onClose={() => {
            setShowEditModal(false);
            setSelectedRole(null);
          }}
          togglePermission={(perm) => togglePermission(perm, false)}
        />
      )}

      {/* Assign Role Modal */}
      {showAssignModal && selectedRole && (
        <AssignRoleModal
          role={selectedRole}
          users={users}
          onAssign={handleAssignRole}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedRole(null);
          }}
        />
      )}
    </div>
  );
};

// Role Modal Component
const RoleModal = ({ title, role, onChange, onSave, onClose, togglePermission }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name *
              </label>
              <input
                type="text"
                value={role.name}
                onChange={(e) => onChange({ ...role, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                placeholder="e.g. Marketing Manager"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon
              </label>
              <input
                type="text"
                value={role.icon}
                onChange={(e) => onChange({ ...role, icon: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                placeholder="lock"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={role.description}
              onChange={(e) => onChange({ ...role, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
              rows="3"
              placeholder="Describe what this role can do..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex gap-2">
              {['red', 'blue', 'green', 'purple', 'yellow', 'indigo', 'gray'].map(color => (
                <button
                  key={color}
                  onClick={() => onChange({ ...role, color })}
                  className={`w-8 h-8 rounded-full border-2 ${
                    role.color === color ? 'border-gray-900' : 'border-transparent'
                  } bg-${color}-500`}
                />
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Permissions * (Selected: {role.permissions?.length || 0})
            </label>
            <div className="space-y-4">
              {Object.entries(PERMISSION_CATEGORIES).map(([key, category]) => (
                <div key={key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AdminIcon name={category.icon} size="sm" className="text-[#0c0f14]" />
                    <h4 className="font-semibold text-gray-900">{category.name}</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {category.permissions.map(permission => (
                      <label
                        key={permission}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={role.permissions?.includes(permission)}
                          onChange={() => togglePermission(permission)}
                          className="w-4 h-4 text-[#5c6670] border-gray-300 rounded focus:ring-[#0c0f14]/20"
                        />
                        <span className="text-sm text-gray-700">
                          {getPermissionLabel(permission)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-6 py-2 admin-btn-primary rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Role
          </button>
        </div>
      </div>
    </div>
  );
};

// Assign Role Modal Component
const AssignRoleModal = ({ role, users, onAssign, onClose }) => {
  const adminUsers = users.filter(u => u.role === 'admin');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">
            Assign "{role.name}" Role
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {adminUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No admin users found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {adminUsers.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={user.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      {user.adminRole === role.id && (
                        <span className="text-xs text-green-600 inline-flex items-center gap-1">
                          <Check className="w-3 h-3" /> Has this role
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onAssign(user.id, role.id)}
                    disabled={user.adminRole === role.id}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      user.adminRole === role.id
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'admin-btn-primary hover:opacity-90'
                    }`}
                  >
                    {user.adminRole === role.id ? 'Assigned' : 'Assign'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;

